import { AuthenticationResult } from '@azure/msal-browser';
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { CredentialResponse } from '@react-oauth/google';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router';

import {
  getPasswordRules,
  isPasswordValid,
} from 'common/src/validators/passwordValidation';

import useAuth from 'portal/src/auth/useAuth';
import { getUserStatusService } from 'portal/src/api/UsersService';
import PasswordRulesDisplay from 'portal/src/components/common/PasswordRulesDisplay';
import { MESSAGES } from 'portal/src/constants/snackbarMessages';
import usePageTitle from 'portal/src/navigation/usePageTitle';
import GoogleLoginButton from 'portal/src/components/auth/GoogleLoginButton';
import MicrosoftLoginButton from 'portal/src/components/auth/MicrosoftLoginButton';
import useSnackbarHandler from 'portal/src/utils/Snackbar/useSnackbarHandler';

import UserLoadingError from './UserLoadingError';

export default function Welcome() {
  usePageTitle();

  const snackbarHandler = useSnackbarHandler();
  const { id } = useParams();
  const [loadingSignUp, setLoadingSignUp] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [externalError, setExternalError] = useState<boolean>(false);
  const { googleLogin, isAuthenticated, microsoftLogin, signup } = useAuth();

  const {
    data: userStatus,
    isLoading: loadingUser,
    isError: userStatusError,
    refetch: refetchUserStatus,
  } = useQuery({
    queryKey: ['userStatus', id],
    queryFn: () => getUserStatusService(id),
  });

  const { valid, results: constraints } = isPasswordValid(
    password,
    confirm,
    userStatus?.passwordRuleOverrides ?? getPasswordRules(),
  );

  const handleGoogleLogin = async (credentialResponse: CredentialResponse) => {
    setLoadingSignUp(true);
    if (!(await googleLogin(credentialResponse.credential)))
      setExternalError(true);
    setLoadingSignUp(false);
  };

  const handleMicrosoftLogin = async (
    authenicationResult: AuthenticationResult,
  ) => {
    setLoadingSignUp(true);
    if (!(await microsoftLogin(authenicationResult.accessToken)))
      setExternalError(true);
    setLoadingSignUp(false);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!valid) return;

    const callSignup = async () => {
      if (!(await signup(id, password))) {
        snackbarHandler.error(MESSAGES.GENERIC.ERROR);
      } else {
        snackbarHandler.success(MESSAGES.GENERIC.SIGNUP_SUCCESS);
      }
      setLoadingSignUp(false);
    };

    setLoadingSignUp(true);
    callSignup();
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setIsDirty(true);
    const field = e.currentTarget.name;
    const value = e.currentTarget.value;
    field === 'password' ? setPassword(value) : setConfirm(value);
  };

  if (isAuthenticated()) {
    return <Navigate to="/" replace />;
  }

  if (userStatusError) {
    return <UserLoadingError onRefreshClick={refetchUserStatus} />;
  }

  if ((!loadingUser && !userStatus?.exists) || userStatus?.signedUp) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      {loadingUser ? (
        <Backdrop open={loadingUser}>
          <CircularProgress aria-label="Loading" />
        </Backdrop>
      ) : (
        <>
          <Typography variant="h4" textAlign={'center'} marginTop={5}>
            Welcome to Nimble Energy
          </Typography>
          <Typography variant="body1" textAlign={'center'}>
            Create a password for your account
          </Typography>
          <br />
          <form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Stack spacing={2} alignItems="center">
              <Grid
                container
                display="flex"
                justifyContent={'center'}
                size={12}
              >
                <PasswordRulesDisplay
                  constraints={constraints}
                  fieldsAreDirty={isDirty}
                />
              </Grid>
              <TextField
                label="Password*"
                name="password"
                value={password}
                onChange={handleChange}
                sx={{
                  width: 350,
                }}
                type="password"
                variant="outlined"
              />
              <TextField
                label="Confirm password*"
                name="confirm"
                value={confirm}
                onChange={handleChange}
                sx={{
                  width: 350,
                }}
                type="password"
                variant="outlined"
              />
              <Button type="submit" variant="contained" disabled={!valid}>
                Create Password
              </Button>
              <Box width={'20rem'}>
                <Divider
                  sx={{
                    mt: 1,
                  }}
                >
                  Or
                </Divider>
              </Box>
              <GoogleLoginButton
                onSuccess={handleGoogleLogin}
                onError={() => {
                  setExternalError(true);
                }}
              />
              <MicrosoftLoginButton
                onSuccess={handleMicrosoftLogin}
                onError={() => setExternalError(true)}
              />
              {externalError && (
                <Typography textAlign={'center'} sx={{ color: 'error.main' }}>
                  Unable to sign up. We do not recognize that account.
                </Typography>
              )}
            </Stack>
          </form>
          <Backdrop open={loadingSignUp}>
            <CircularProgress aria-label="Loading" />
          </Backdrop>
        </>
      )}
    </>
  );
}
